import styled from "styled-components";
import {ws} from "styles/tokens";

/**
 * Onboarding palette, mapped onto the Waterspeed design system tokens.
 * Cards and controls use Inter; headlines use Work Sans (light/regular, tight tracking).
 */
export const ONBOARDING_COLORS = {
  accent: ws.accent,
  accentBright: ws.accentText,
  accentDeep: ws.accentDeep,
  titleDark: ws.fg,
  label: ws.fg,
  subtitle: ws.muted,
  placeholder: ws.soft,
  border: ws.line,
  bodyText: ws.muted,
  panelBg: ws.wash,
  rowBg: ws.wash,
  successBg: ws.successBg
};

/** Solid accent fill: the design system has one teal and no gradients on controls. */
export const ACCENT_GRADIENT = ws.accent;

export const GradientPage = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(175deg, ${ws.accentDeep} 3%, ${ws.night} 96%);
`;

export const WhitePage = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${ws.bg};
`;

export const LandingContainer = styled.main`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

export const CenteredContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 48px 16px;
`;

export const OnboardingCard = styled.div<{maxWidth?: string; alignItems?: string; gap?: string}>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => props.alignItems || "center"};
  gap: ${(props) => props.gap || "24px"};
  width: 100%;
  max-width: ${(props) => props.maxWidth || "480px"};
  padding: 48px;
  background: ${ws.card};
  border-radius: ${ws.radiusXl};
  box-shadow: ${ws.shadowMenu};

  @media (max-width: 560px) {
    padding: 32px 24px;
  }
`;

export const CardTitle = styled.h1<{fontSize?: string}>`
  margin: 0;
  font-family: ${ws.fontDisplay};
  font-size: ${(props) => props.fontSize || "24px"};
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${ONBOARDING_COLORS.titleDark};
  text-align: center;
`;

export const CardSubtitle = styled.p<{color?: string; fontSize?: string}>`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: ${(props) => props.fontSize || "15px"};
  font-weight: 400;
  color: ${(props) => props.color || ONBOARDING_COLORS.subtitle};
  text-align: center;
`;

/* Apple keeps its black brand button; Google and email are outlined pills */
export const SocialButton = styled.button<{variant: "apple" | "google" | "email"}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.variant === "email" ? "14px" : "12px")};
  width: 100%;
  padding: ${(props) => (props.variant === "email" ? "14px" : "16px")};
  border-radius: ${ws.radiusPill};
  font-family: ${ws.fontBody};
  font-size: ${(props) => (props.variant === "email" ? "16px" : "15px")};
  font-weight: 600;
  letter-spacing: ${(props) => (props.variant === "email" ? "-0.01em" : "normal")};
  cursor: pointer;
  background: ${(props) => (props.variant === "apple" ? "#000" : ws.card)};
  color: ${(props) => (props.variant === "apple" ? "#fff" : ws.fg)};
  border: ${(props) => (props.variant === "apple" ? "1px solid transparent" : `1px solid ${ws.lineStrong}`)};
  transition:
    border-color 0.2s,
    opacity 0.2s;

  &:hover {
    border-color: ${(props) => (props.variant === "apple" ? "transparent" : ws.fg)};
    opacity: ${(props) => (props.variant === "apple" ? 0.9 : 1)};
  }
`;

export const GradientCtaButton = styled.button<{small?: boolean}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: ${(props) => (props.small ? "336px" : "370px")};
  height: ${(props) => (props.small ? "51px" : "56px")};
  padding: 0 ${(props) => (props.small ? "36px" : "40px")};
  border: none;
  border-radius: ${ws.radiusPill};
  background: ${ws.accent};
  font-family: ${ws.fontBody};
  font-size: ${(props) => (props.small ? "14px" : "17px")};
  font-weight: 600;
  line-height: 21px;
  color: ${ws.onAccent};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${ws.accentDeep};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/* The Figma arrow asset points up; the CTA design rotates it to point right */
export const CtaArrowIcon = styled.img`
  width: 18px;
  height: 18px;
  transform: rotate(90deg);
`;

export const DividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${ONBOARDING_COLORS.border};
  }
`;

export const DividerLabel = styled.span`
  font-family: ${ws.fontBody};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ONBOARDING_COLORS.placeholder};
`;

export const FinePrint = styled.p`
  margin: 0;
  width: 100%;
  font-family: ${ws.fontBody};
  font-size: 13px;
  font-weight: 400;
  color: ${ONBOARDING_COLORS.placeholder};
  text-align: center;
`;

export const SwitchRow = styled.p`
  margin: 0;
  font-family: ${ws.fontBody};
  font-size: 13px;
  color: ${ws.muted};
  text-align: center;
`;

export const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: ${ws.fontBody};
  font-size: 13px;
  font-weight: 600;
  color: ${ONBOARDING_COLORS.accentBright};
  cursor: pointer;
`;

/* Pill inputs on the field surface */
export const FormLabel = styled.label`
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${ws.fg};
`;

export const FormInputBox = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 370px;
  height: 60px;
  padding: 0 27px;
  background: ${ws.field};
  border: 1px solid transparent;
  border-radius: ${ws.radiusPill};

  &:focus-within {
    border-color: ${ws.accent};
  }
`;

export const FormInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${ws.fontBody};
  font-size: 17px;
  color: ${ws.fg};

  &::placeholder {
    color: ${ws.muted};
  }
`;

/* Photo cards (newsletter / trial / ready screens): image on top, rounded panel below */
export const PhotoCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  border-radius: ${ws.radiusXl};
  background: ${ws.card};
  box-shadow: ${ws.shadowMenu};
  overflow: hidden;
`;

export const CardPhoto = styled.img`
  width: 100%;
  height: 279px;
  object-fit: cover;
`;

export const InnerPanel = styled.div<{gap?: string}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.gap || "30px"};
  width: 100%;
  margin-top: -28px;
  padding: 38px 48px 27px;
  background: ${ws.card};
  border-radius: ${ws.radiusXl};
  z-index: 1;
`;

export const PanelTitle = styled.h1`
  margin: 0;
  font-family: ${ws.fontDisplay};
  font-size: 29px;
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${ws.fg};
  text-align: center;
`;

export const PanelText = styled.p`
  margin: 0;
  max-width: 332px;
  font-family: ${ws.fontBody};
  font-size: 17px;
  color: ${ws.muted};
  text-align: center;
`;

/* Secondary CTA: outlined pill */
export const AccentTextButton = styled.button`
  padding: 10px 22px;
  border: 1px solid ${ws.lineStrong};
  border-radius: ${ws.radiusPill};
  background: transparent;
  font-family: ${ws.fontBody};
  font-size: 15px;
  font-weight: 600;
  color: ${ws.fg};
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${ws.fg};
  }
`;

export const InputFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const InputLabel = styled.label`
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${ONBOARDING_COLORS.label};
`;

export const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px 20px;
  background: ${ws.field};
  border: 1px solid transparent;
  border-radius: ${ws.radiusPill};

  &:focus-within {
    border-color: ${ONBOARDING_COLORS.accent};
  }
`;

export const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${ws.fontBody};
  font-size: 15px;
  color: ${ONBOARDING_COLORS.titleDark};

  &::placeholder {
    color: ${ws.muted};
  }
`;

export const TextLinkButton = styled.button<{color?: string; underline?: boolean}>`
  padding: 0;
  border: none;
  background: none;
  font-family: ${ws.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.color || ws.accentText};
  text-decoration: ${(props) => (props.underline ? "underline" : "none")};
  cursor: pointer;
`;
